import React, { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Maybe } from '@graphql-tools/utils'
import type { UserInteractionEvent, ViewModeChangedEvent } from 'bitmovin-player'
import classNames from 'classnames'
import { When } from 'react-if'
import { slugs } from '@/constants/slugs'
import { MomentStoryProvider } from '@/contexts/MomentStoryContext'
import { useLatestRef } from '@/hooks/useLatestRef'
import {
  AngelPlayerContextProvider,
  AssetDescription,
  PlayerOverlay,
  SmpteController,
  ViewMode,
} from '@/organisms/AngelPlayer'
import { BitmovinPlayer } from '@/organisms/AngelPlayer/BitmovinPlayer'
import { useAttributionPlayer } from '@/organisms/AttributionPlayer/useAttributionPlayer'
import { MomentStoryModalProvider } from '@/organisms/CollectibleSceneInfo/MomentStoryModal'
import { CollectiblesPlayerProvider } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerContext'
import {
  EngagementDetail,
  useCollectiblesPlayerEngagement,
} from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { CollectiblesPlayerEngagementOverlay } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementOverlay'
import { CollectiblesPlayerEngagementPanel } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementPanel/CollectiblesPlayerEngagementPanel'
import { CollectiblesPlayerInformationSection } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerInformationSection'
import { CollectiblesPlayerPlaceHolder } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerPlaceHolder'
import { usePlayerMoments } from '@/organisms/CollectiblesPlayer/PlayerMomentsProvider'
import { incrementUserViewCount } from '@/organisms/GetAppBanner/GetAppBannerUtil'
import { CatalogTitle, findHydraExternalId, hasHydraIdAndSlug } from '@/services/ContentCatalog'
import { useGuildMember } from '@/services/GuildUserService'
import { Episode, GetEpisodeResponse, WatchableAvailabilityContent } from '@/services/ProjectsService'
import { ContentEpisode, NextContentEpisodeSpecialOrMovie } from '@/services/ProjectsService/types'
import { useUser } from '@/services/UserService'
import { useStrictModeDetector } from '@/services/WatchableService/hooks'
import { Frame } from '@/types/codegen-federation'
import { buildProjectEpisodeTitle, isASneakPeek } from '@/utils/EpisodeUtil'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { getAnalyticsSessionId } from '@/utils/analytics/session-utils'
import { logger } from '@/utils/logging'
import { usePerfLogs } from '@/utils/usePerfLogs'
import { SayThankYouModal } from '@/views/SayThankYouForm/SayThankYouModal'
import { ContentShareCTA } from '@/views/WatchEpisodeView/ContentShareCTA'
import { EpisodeOverlay } from '@/views/WatchEpisodeView/EpisodeOverlay'
import { SkipIntro } from '@/views/WatchEpisodeView/SkipIntro'
import { SkipIntroOverlay } from '@/views/WatchEpisodeView/SkipIntro/SkipIntroOverlay'
import { SneakPeekCTA } from '@/views/WatchEpisodeView/SneakPeekCTA'
import { useEpisodeAsset } from '@/views/WatchEpisodeView/useEpisodeAsset'
import { AngelPlayerEvent } from '../AngelPlayer/AngelPlayerContext/AngelPlayerContext'
import { ParentalControlsModal } from '../ParentalControlsModal'
import { AccountRequiredOverlay } from './AccountRequiredOverlay'
import { FrameProvider } from './FrameContext'
import { SignedFrameOverlay } from './SignedFrameOverlay'

const canShowGuildCTA = (isGuildMember: boolean, projectSlug: string, episode: Episode) => {
  return (
    ((!isGuildMember && projectSlug === slugs.hisOnlySon) ||
      projectSlug === slugs.afterDeath ||
      projectSlug === slugs.soundOfHopeTheStoryOfPossumTrot ||
      projectSlug === slugs.sight ||
      projectSlug === slugs.cabrini ||
      projectSlug === slugs.theShift) &&
    (episode.isTrailer ||
      episode?.name?.toLowerCase().includes('sneak') ||
      episode?.name?.toLowerCase().includes('preview'))
  )
}

interface CollectiblesPlayerProps {
  aspectClassName?: string
  autoplay?: boolean
  children?: ReactNode | undefined
  className?: string
  data?: GetEpisodeResponse
  episode: Episode
  contentEpisode?: ContentEpisode
  onStart?: () => void
  overlays?: PlayerOverlay[]
  playerContainerClassName?: string
  shouldSaveWatchPosition?: boolean
  smpteTimestamp?: string
  defaultEngagementDetail: Maybe<EngagementDetail>
  skipAttribution?: boolean
  skipAds?: boolean
  frame?: Frame
  defaultCommunityView?: 'on' | 'off'
  isLoginRequired: boolean
  userId?: string | null | undefined
  projectTitle?: CatalogTitle | null
  watchableTitle?: CatalogTitle | null
  isSellingTickets: boolean
  watchableContent?: WatchableAvailabilityContent
  setShowShareModal: (showShareModal: boolean) => void
  setShowParentalControlsModal: (showParentalControlsModal: boolean) => void
  showParentalControlsModal: boolean
  hasSkipLanguagesAvailable: boolean
  userEnabledParentalControls: boolean
  setUserSwearingEnabledPreference?: (swearingPreference: boolean) => Promise<null>
  showParentalControlsExperiment: boolean
  nextContent?: NextContentEpisodeSpecialOrMovie | null
}

const InternalCollectiblesPlayer: FC<CollectiblesPlayerProps> = ({
  autoplay = false,
  className,
  data,
  episode,
  contentEpisode,
  shouldSaveWatchPosition = false,
  defaultEngagementDetail,
  skipAttribution,
  skipAds,
  frame,
  defaultCommunityView,
  isLoginRequired,
  userId,
  projectTitle,
  watchableTitle,
  isSellingTickets,
  watchableContent,
  setShowShareModal,
  setShowParentalControlsModal,
  showParentalControlsModal,
  hasSkipLanguagesAvailable = false,
  userEnabledParentalControls = false,
  setUserSwearingEnabledPreference,
  showParentalControlsExperiment,
  nextContent,
  ...playerProps
}) => {
  usePerfLogs('CollectiblesPlayer')
  const { track, user } = useSafeAnalytics()
  const { isGuildMember } = useGuildMember()
  const { isLoggedIn } = useUser()
  const { locale } = useLocale()
  const sessionId = getAnalyticsSessionId()
  const [isFullScreen, setIsFullScreen] = useState(false)
  const { updateEngagementMoment } = useCollectiblesPlayerEngagement()
  const { canDisplayScenes, moments, getMoment } = usePlayerMoments()
  const [currentTime, setCurrentTime] = useState<number>(defaultEngagementDetail?.startTime || 0)
  const [controller, setController] = useState<SmpteController>()
  const { asset } = useEpisodeAsset(
    { ...data },
    { defaultEngagementDetail },
    userEnabledParentalControls,
    controller,
    contentEpisode,
    nextContent,
    watchableContent,
  )
  const [isAdPlaying, setIsAdPlaying] = useState(false)
  const [canShowEpisodeOverlay, setCanShowEpisodeOverlay] = useState(false)
  const [hasDismissedOverlay, setHasDismissedOverlay] = useState(false)
  const isStrict = useStrictModeDetector()
  const ref = useRef<NodeJS.Timeout>()

  const showSneakPeekCTA = shouldShowSneakPeek(episode, isGuildMember, isSellingTickets)
  const showEndOfAsset = shouldShowEndOfAsset(data, episode, isLoggedIn)
  const enableAds = !skipAds && !showSneakPeekCTA

  const { showThankYouModal, handleModalCancel, handleModalSave, benefactor } = useAttributionPlayer({
    asset,
    skip: skipAttribution,
  })
  const hasSignedFrame = Boolean(frame?.nifty?.signatures?.[0]?.signatureUrl)
  const [showSignedFrameOverlay, setShowSignedFrameOverlay] = useState(hasSignedFrame || !!frame)
  const [displayFrameDetails, setDisplayFrameDetails] = useState(!!frame?.id)

  const didLoadAsset = useRef(false)
  const assetRef = useLatestRef(asset)

  const currentAssetUrl = asset?.sourceConfig?.hls

  useEffect(() => {
    const loadAndPlayAsset = async () => {
      if (!isLoginRequired && controller && assetRef.current) {
        try {
          if (didLoadAsset.current) {
            await controller.unload()
          }
          didLoadAsset.current = true
          await controller.load(assetRef.current as unknown as AssetDescription)
          if (autoplay) {
            controller.play()
          }
        } catch (error) {
          logger().warn('Error loading asset:', error)
        }
      }
    }

    loadAndPlayAsset()
  }, [currentAssetUrl, autoplay, controller, isLoginRequired, assetRef])

  useEffect(() => {
    if (!isLoginRequired && benefactor) {
      const mobileAppBanner = document.getElementById('get-app-banner') || null
      document.documentElement.classList.add('overflow-y-hidden')
      mobileAppBanner && (mobileAppBanner.style.display = 'none')
      ref.current = setTimeout(() => {
        document.documentElement.classList.remove('overflow-y-hidden')
        mobileAppBanner && (mobileAppBanner.style.display = 'block')
        controller?.play()
      }, 6000)
      if (showThankYouModal) {
        clearTimeout(ref.current as NodeJS.Timeout)
      }
    }

    return () => {
      clearTimeout(ref.current as NodeJS.Timeout)
      document.documentElement.classList.remove('overflow-y-hidden')
    }
  }, [controller, showThankYouModal, benefactor, isLoginRequired])

  const handleViewChanged = useCallback(
    (event: ViewModeChangedEvent) => {
      setIsFullScreen(event.to === ViewMode.Fullscreen)
    },
    [setIsFullScreen],
  )

  const handlePifButtonClick = useCallback(() => {
    track('Clicked Pay It Forward From Episode View', {
      program: episode.projectSlug,
      asset_id: episode.id,
      title: buildProjectEpisodeTitle({
        projectType: data?.project?.projectType,
        seasonNumber: data?.episode?.seasonNumber as number,
        episodeNumber: data?.episode?.episodeNumber as number,
        projectName: data?.project?.name as string,
      }),
      subtitle: data?.episode?.subtitle,
      season: data?.episode?.seasonNumber,
      episode: data?.episode?.episodeNumber,
    })
  }, [data, episode, track])

  const handleGuildButtonClick = useCallback(() => {
    track('Guild Watch Clicked', {
      context: 'episode view',
      program: episode.projectSlug,
      asset_id: episode.id,
      title: buildProjectEpisodeTitle({
        projectType: data?.project?.projectType,
        seasonNumber: data?.episode?.seasonNumber as number,
        episodeNumber: data?.episode?.episodeNumber as number,
        projectName: data?.project?.name as string,
      }),
      season: data?.episode?.seasonNumber,
      episode: data?.episode?.episodeNumber,
    })
  }, [data, episode, track])

  const onPause = useCallback(
    ({ issuer }: UserInteractionEvent, { hasPlayed, time }: AngelPlayerEvent) => {
      const isProperEngagement = hasPlayed || (!hasPlayed && defaultEngagementDetail)
      if (!isProperEngagement) return
      if (!moments?.length || issuer === 'ui-seek') return
      const currentMoment = getMoment({ time })

      if (!currentMoment) return
      updateEngagementMoment(currentMoment)
      setCurrentTime(time || 0)
    },
    [defaultEngagementDetail, moments?.length, getMoment, updateEngagementMoment],
  )

  const onPlay = useCallback(
    ({ time, uiManager }: AngelPlayerEvent) => {
      const currentMoment = getMoment({ time })
      setDisplayFrameDetails(false)
      if (!currentMoment) return

      const duration = (currentMoment?.endTime || 0) - (currentMoment?.startTime || 0)
      uiManager?.removeTimelineMarker({
        time: currentMoment.startTime || 0,
        markerType: 'scene',
        sceneType: currentMoment?.type || 'COMMON',
        duration,
      })
    },
    [getMoment],
  )

  const overlays = useMemo(() => {
    const fullScreenOverlays = [
      {
        key: 'engagement-panel',
        Component: CollectiblesPlayerEngagementOverlay,
      },
      {
        key: 'skip-intro-button',
        Component: SkipIntroOverlay,
      },
    ]

    return isFullScreen ? fullScreenOverlays : []
  }, [isFullScreen])

  const handleControllerInit = useCallback((smpteController: SmpteController) => {
    setController(smpteController)
  }, [])

  const getControlBarElement = useCallback(() => {
    return document.getElementsByClassName('bmpui-ui-uicontainer')
  }, [])

  const hidePlayerControls = useCallback(() => {
    try {
      const controlBarElement = getControlBarElement()
      const element = controlBarElement.item(0)
      if (element && !element.classList.contains('hidden')) {
        element.classList.add('hidden')
        logger().debug('Player controls hidden')
      }
    } catch (error) {
      logger().warn('Failed to hide player controls:', error)
    }
  }, [getControlBarElement])

  const showPlayerControls = useCallback(() => {
    try {
      const controlBarElement = getControlBarElement()
      const element = controlBarElement.item(0)
      if (element && element.classList.contains('hidden')) {
        element.classList.remove('hidden')
        logger().debug('Player controls shown')
      }
    } catch (error) {
      logger().warn('Failed to show player controls:', error)
    }
  }, [getControlBarElement])

  const onAdStarted = useCallback(() => {
    track('Ad Started', {
      guid: episode?.guid,
    })

    hidePlayerControls()
    setIsAdPlaying(true)
  }, [track, episode?.guid, hidePlayerControls])

  const onAdFinished = useCallback(() => {
    track('Ad Finished', {
      guid: episode?.guid,
    })

    showPlayerControls()
    setIsAdPlaying(false)
  }, [track, episode?.guid, showPlayerControls])

  const onAdSkipped = useCallback(() => {
    setIsAdPlaying(false)
    track('Skipped Ad', {
      guid: episode?.guid,
    })
    showPlayerControls()
  }, [track, episode?.guid, showPlayerControls])

  if (!asset) {
    return <CollectiblesPlayerPlaceHolder className={className} />
  }

  const hasPayItForward = data?.project?.pifEnabled || false
  const hasIntro = Boolean(episode?.introStartTime && episode?.introEndTime)
  const isSignedFrameShowing = !isLoginRequired && frame && showSignedFrameOverlay
  const handleShowSignedFrameOverlay = () => {
    setShowSignedFrameOverlay(false)
    controller?.play()
  }
  const isGuildShareAvailable = data?.project?.title?.isGuildShareAvailable

  const canSeeShareButton = Boolean(
    isGuildShareAvailable &&
      watchableTitle &&
      hasHydraIdAndSlug(watchableTitle) &&
      isGuildMember &&
      episode?.id &&
      findHydraExternalId(watchableTitle) === episode.id,
  )

  const showParentalControlsButton = showParentalControlsExperiment && hasSkipLanguagesAvailable
  const parentalControlsEnabled = userEnabledParentalControls

  return (
    <AngelPlayerContextProvider
      asset={asset}
      episodeGuid={episode?.guid}
      onStart={incrementUserViewCount}
      onPause={onPause}
      onPlay={onPlay}
      onControllerInit={handleControllerInit}
      startTime={defaultEngagementDetail?.startTime}
      introStartTime={episode?.introStartTime}
      introEndTime={episode?.introEndTime}
      shouldSaveWatchPosition={shouldSaveWatchPosition}
      defaultCommunityView={defaultCommunityView}
      setShowShareModal={setShowShareModal}
      setShowParentalControlsModal={setShowParentalControlsModal}
      isAdPlaying={isAdPlaying}
      hidePlayerControls={hidePlayerControls}
      showPlayerControls={showPlayerControls}
    >
      <CollectiblesPlayerProvider>
        <FrameProvider frame={displayFrameDetails ? frame : undefined}>
          <div
            data-testid="angel-player"
            className={classNames('angel-player overflow-x-hidden animate-fadein', className)}
          >
            <div
              className={classNames('relative bg-core-gray-950', {
                'min-h-[312px]': isLoginRequired,
                flex: !isSignedFrameShowing,
              })}
            >
              <MomentStoryProvider>
                <MomentStoryModalProvider user={defaultEngagementDetail?.user}>
                  <div
                    className={classNames('z-40 transition-all duration-[2000ms]', {
                      'basis-full': !isSignedFrameShowing,
                    })}
                  >
                    <div
                      className={classNames('aspect-h-9 relative', {
                        'lg:flex lg:h-[75vh]': !isSignedFrameShowing,
                      })}
                    >
                      <div
                        className={classNames(
                          isSignedFrameShowing ? 'grid grid-cols-1 lg:grid-cols-[1fr_auto]' : 'w-full',
                        )}
                      >
                        <div
                          className={classNames(
                            'lg:h-[75vh] lg:flex',
                            isSignedFrameShowing ? 'relative' : 'aspect-h-9 relative',
                          )}
                        >
                          <div
                            className={classNames(
                              canShowEpisodeOverlay && !hasDismissedOverlay
                                ? 'transition-all ease-in-out aspect-h-9 w-full lg:w-[329px] lg:h-[185px] lg:z-30 lg:absolute lg:top-10 lg:right-12 lg:rounded-2xl lg:overflow-hidden'
                                : 'transition-all ease-in-out aspect-h-9 w-full h-full lg:absolute top-0 right-0',
                            )}
                            style={{
                              transitionDuration: '750ms',
                            }}
                          >
                            <BitmovinPlayer
                              autoplay={autoplay}
                              disableAssetLoad
                              anonymousUserId={user()?.anonymousId}
                              loggedInUserId={userId}
                              enableAds={enableAds}
                              overlays={overlays}
                              onViewModeChanged={handleViewChanged}
                              {...playerProps}
                              asset={asset}
                              isStrict={isStrict}
                              onAdStarted={onAdStarted}
                              onAdFinished={onAdFinished}
                              onAdSkipped={onAdSkipped}
                              disableCommunityView={!canDisplayScenes}
                              isLoginRequired={isLoginRequired}
                              showContentShareButton={canSeeShareButton}
                              showParentalControlsButton={showParentalControlsButton}
                              parentalControlsEnabled={parentalControlsEnabled}
                              uiLanguage={locale}
                              sessionId={sessionId}
                            />
                          </div>
                          {showEndOfAsset && (
                            <EpisodeOverlay
                              canShowEpisodeOverlay={canShowEpisodeOverlay}
                              projectTitle={projectTitle}
                              isGuildMember={isGuildMember}
                              episode={episode}
                              hidePlayerControls={hidePlayerControls}
                              showPlayerControls={showPlayerControls}
                              setCanShowEpisodeOverlay={setCanShowEpisodeOverlay}
                              hasDismissedOverlay={hasDismissedOverlay}
                              setHasDismissedOverlay={setHasDismissedOverlay}
                              setShowShareModal={setShowShareModal}
                              project={data?.project}
                              isAdPlaying={isAdPlaying}
                              watchPosition={contentEpisode?.watchPosition?.position}
                              watchableTitle={watchableTitle}
                              isGuildShareAvailable={isGuildShareAvailable}
                              desktopView
                            />
                          )}
                          {hasIntro && <SkipIntro episode={episode} isAdPlaying={isAdPlaying} />}
                          {isSignedFrameShowing && frame && (
                            <SignedFrameOverlay
                              frame={frame}
                              hasSignedFrame={hasSignedFrame}
                              handleClick={handleShowSignedFrameOverlay}
                            />
                          )}
                        </div>
                        {isSignedFrameShowing && (
                          <CollectiblesPlayerEngagementPanel
                            currentTime={currentTime}
                            frame={frame}
                            controller={controller}
                          />
                        )}
                      </div>
                      {!isSignedFrameShowing && !isLoginRequired && (
                        <CollectiblesPlayerEngagementPanel
                          currentTime={currentTime}
                          frame={frame}
                          controller={controller}
                        />
                      )}
                    </div>
                  </div>
                </MomentStoryModalProvider>
              </MomentStoryProvider>
              {isLoginRequired && <AccountRequiredOverlay episode={episode} project={data?.project} />}
              {showSneakPeekCTA && <SneakPeekCTA episode={episode} project={data?.project} />}
              {isGuildMember && !showEndOfAsset && (
                <ContentShareCTA
                  episode={episode}
                  project={data?.project}
                  moviePosterPath={
                    data?.project?.title?.portraitAngelImage?.cloudinaryPath ?? episode?.posterCloudinaryPath
                  }
                  setShowShareModal={setShowShareModal}
                  watchableTitle={watchableTitle}
                />
              )}
            </div>

            {showEndOfAsset && (
              <div className="h-full w-full lg:hidden">
                <EpisodeOverlay
                  canShowEpisodeOverlay={canShowEpisodeOverlay}
                  projectTitle={projectTitle}
                  isGuildMember={isGuildMember}
                  episode={episode}
                  hidePlayerControls={hidePlayerControls}
                  showPlayerControls={showPlayerControls}
                  setCanShowEpisodeOverlay={setCanShowEpisodeOverlay}
                  hasDismissedOverlay={hasDismissedOverlay}
                  setHasDismissedOverlay={setHasDismissedOverlay}
                  setShowShareModal={setShowShareModal}
                  project={data?.project}
                  isAdPlaying={isAdPlaying}
                  watchPosition={contentEpisode?.watchPosition?.position}
                  watchableTitle={watchableTitle}
                  isGuildShareAvailable={isGuildShareAvailable}
                />
              </div>
            )}
            <When condition={showParentalControlsExperiment}>
              <ParentalControlsModal
                controller={controller}
                episodeName={data?.project?.name || episode.subtitle}
                projectSlug={episode.projectSlug}
                isModalOpen={showParentalControlsModal}
                setIsModalOpen={setShowParentalControlsModal}
                userEnabledParentalControls={userEnabledParentalControls}
                setUserSwearingEnabledPreference={setUserSwearingEnabledPreference}
              />
            </When>

            <CollectiblesPlayerInformationSection
              description={episode.description}
              handlePifButtonClick={handlePifButtonClick}
              hasPayItForward={hasPayItForward}
              name={episode?.name}
              projectSlug={episode.projectSlug}
              projectType={data?.project?.projectType}
              subtitle={episode.subtitle}
              shouldShowGuildCTA={canShowGuildCTA(isGuildMember, episode.projectSlug, episode)}
              handleGuildButtonClick={handleGuildButtonClick}
              episode={episode}
              setShowShareModal={setShowShareModal}
              isSellingTickets={isSellingTickets}
              watchableTitle={watchableTitle}
              isGuildShareAvailable={isGuildShareAvailable}
            />
          </div>
          <SayThankYouModal
            show={showThankYouModal}
            onCancel={handleModalCancel}
            onSave={handleModalSave}
            projectSlug={asset.projectSlug as string}
          />
        </FrameProvider>
      </CollectiblesPlayerProvider>
    </AngelPlayerContextProvider>
  )
}

export const CollectiblesPlayer = React.memo(InternalCollectiblesPlayer)

CollectiblesPlayer.displayName = 'React.memo(InternalCollectiblesPlayer)'

const shouldShowEndOfAsset = (data: GetEpisodeResponse | undefined, episode: Episode, isLoggedIn: boolean): boolean => {
  return Boolean(
    (data?.project?.projectType === 'movie' ||
      data?.project?.projectType === 'series' ||
      data?.project?.projectType === 'special') &&
      isLoggedIn &&
      !episode?.upNext?.episodeNumber,
  )
}

const shouldShowSneakPeek = (episode: Episode, isGuildMember: boolean, isSellingTickets: boolean): boolean => {
  return isASneakPeek(episode) && (!isGuildMember || isSellingTickets)
}
